import { useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';

import { useNavigateToLastOrderMenu, useSweetpassTier } from '@order/hooks';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { SupportedHomeHeroActions } from '../../utils';

export const useHomeHeroCTA = (action?: HomeHeroAction) => {
  const handleViewMenu = useNavigateToLastOrderMenu();
  const navigation = useNavigation();
  const { track } = useTelemetry();

  // ─── Sweetpass ───────────────────────────────────────────────────────

  const isSweetpassEnabled = useFeatureFlag('OPS-sweetpass-enabled');
  const { sweetpassTierName, isSweetpassTierLoading } = useSweetpassTier();
  const canUpgradeSweetpass =
    isSweetpassEnabled &&
    !isSweetpassTierLoading &&
    sweetpassTierName !== 'SWEETPASS_PLUS';

  // ─────────────────────────────────────────────────────────────────────

  return useCallback(() => {
    track('home.hero_module');

    if (action === SupportedHomeHeroActions.ViewMenu) {
      handleViewMenu();

      return;
    }

    if (
      action === SupportedHomeHeroActions.UpgradeSweetpass &&
      canUpgradeSweetpass
    ) {
      navigation.navigate('Modal', { screen: 'SweetpassUpgrade' });

      return;
    }

    if (
      action === SupportedHomeHeroActions.ExploreBenefits &&
      isSweetpassEnabled
    ) {
      navigation.navigate('Modal', { screen: 'SweetpassBenefits' });

      return;
    }

    if (
      action === SupportedHomeHeroActions.ExploreSweetpass &&
      isSweetpassEnabled
    ) {
      navigation.navigate('MainTabs', {
        screen: 'LoyaltyTab',
        params: { screen: 'SweetpassHome' },
      });

      return;
    }

    if (
      action === SupportedHomeHeroActions.ExploreChallenges &&
      isSweetpassEnabled
    ) {
      navigation.navigate('MainTabs', {
        screen: 'LoyaltyTab',
        params: { screen: 'SweetpassHome', params: { target: 'challenges' } },
      });

      return;
    }

    if (
      action === SupportedHomeHeroActions.ExploreRewards &&
      isSweetpassEnabled
    ) {
      navigation.navigate('MainTabs', {
        screen: 'LoyaltyTab',
        params: { screen: 'SweetpassHome', params: { target: 'rewards' } },
      });

      return;
    }

    handleViewMenu(); // default handler.
  }, [
    action,
    isSweetpassEnabled,
    canUpgradeSweetpass,
    navigation,
    track,
    handleViewMenu,
  ]);
};

// ─── Types / Constants ─────────────────────────────────────────────────

type ValueOf<T> = T[keyof T];
type HomeHeroAction = ValueOf<typeof SupportedHomeHeroActions>;
